<template>
  <page :name="$t('Gestione utente')" hide-breadcrumbs :loading="anagraficaState.updating">
    <pf-page-section>
      <pf-flex align-items="stretch" gap="md">
        <pf-card style="flex: 1; min-width: 300px">
          <pf-card-header>
            <pf-title h="2">{{ $t('I tuoi dati') }}</pf-title>
          </pf-card-header>

          <pf-card-body>
            <x-form :submit-text="$t('Aggiorna i tuoi dati')" @submit="submit">
              <form-group :name="$t('Nome')">
                <x-input id="anagraficaNome" v-model="anagrafica.firstname" name="firstname" type="text" maxlength="250" autocomplete="firstname" />
              </form-group>

              <form-group :name="$t('Cognome')">
                <x-input id="anagraficaCognome" v-model="anagrafica.lastname" name="lastname" type="text" maxlength="250" autocomplete="lastname" />
              </form-group>

              <form-group :name="$t('Telefono')">
                <x-input id="anagraficaTelefono" v-model="anagrafica.phone" type="tel" maxlength="20" />
              </form-group>

              <form-group :name="$t('Cellulare')">
                <x-input id="anagraficaMobile" v-model="anagrafica.mobile" name="mobile" type="tel" maxlength="20" autocomplete="tel" />
              </form-group>

              <form-group :name="$t('Email (utilizzata anche come username)')" required>
                <x-input id="anagraficaEmail" v-model="anagrafica.mail" name="mail" type="email" required maxlength="100" autocomplete="email" />
                <pf-alert v-if="originalMail !== anagrafica.mail && login.username === originalMail" variant="info" :title="$t('IMPORTANTE')">
                  {{ $t("il nuovo indirizzo email verrà usato anche come username per accedere all'area riservata, al posto di {0}", [login.username]) }}.
                </pf-alert>
              </form-group>

              <form-group
                :name="$t('Notifiche Email')"
                :helper-text="
                  !login.grants.owner
                    ? $t(
                        'Puoi solamente rimuovere eventuali notifiche attive. L\'aggiunta deve essere richiesta direttamente ad un utente amministratore di questa azienda.',
                      )
                    : ''
                "
              >
                <div>
                  <pf-switch
                    :label="$t('Tecniche')"
                    :checked="anagrafica.email_subscriptions.includes('technical')"
                    @update:checked="
                      $event
                        ? anagrafica.email_subscriptions.push('technical')
                        : anagrafica.email_subscriptions.splice(anagrafica.email_subscriptions.indexOf('technical'), 1)
                    "
                    ouia-id="tech-notification-switch"
                    :disabled="!canRemoveSubscriptionOfType('technical')"
                  />
                  {{ ' ' }}
                  <form-help-tooltip :text="$t('Riceve in copia le notifiche tecniche (es. tickets, notifiche di sistema)')" />
                </div>
                <div>
                  <pf-switch
                    :label="$t('Amministrative')"
                    :checked="anagrafica.email_subscriptions.includes('billing')"
                    @update:checked="
                      $event
                        ? anagrafica.email_subscriptions.push('billing')
                        : anagrafica.email_subscriptions.splice(anagrafica.email_subscriptions.indexOf('billing'), 1)
                    "
                    ouia-id="billing-notification-switch"
                    :disabled="!canRemoveSubscriptionOfType('billing')"
                  />
                  {{ ' ' }}
                  <form-help-tooltip :text="$t('Riceve in copia le notifiche amministrative (es. fatture, scadenze)')" />
                </div>
                <div>
                  <pf-switch
                    :label="$t('Commerciali')"
                    :checked="anagrafica.email_subscriptions.includes('marketing')"
                    @update:checked="
                      $event
                        ? anagrafica.email_subscriptions.push('marketing')
                        : anagrafica.email_subscriptions.splice(anagrafica.email_subscriptions.indexOf('marketing'), 1)
                    "
                    ouia-id="marketing-notification-switch"
                    :disabled="!canRemoveSubscriptionOfType('marketing')"
                  />
                  {{ ' ' }}
                  <form-help-tooltip :text="$t('Riceve in copia le notifiche commerciali (es. offerte dedicate)')" />
                </div>
              </form-group>

              <form-group :name="$t('Newsletter')">
                <pf-switch :label="$t('Iscritto alla nostra newsletter')" v-model:checked="anagrafica.newsletter_subscription" ouia-id="newsletter-switch" />
              </form-group>
            </x-form>
          </pf-card-body>
        </pf-card>

        <pf-card style="flex: 1; min-width: 300px">
          <pf-card-header>
            <pf-title h="2">{{ $t('Sicurezza') }}</pf-title>
          </pf-card-header>

          <pf-card-body>
            <pf-title h="4">{{ $t('Cambia Password') }}</pf-title>
            <reset-password-form v-show="!anagraficaState.updating" />

            <pf-form component="div" class="pf-v6-u-mt-3xl">
              <form-group
                :name="$t('Autenticazione a due fattori')"
                :helper-text="$t('Imposta l\'account per ricevere codici di autenticazione tramite un\'applicazione dedicata')"
              >
                <pf-switch
                  v-show="!anagraficaState.updating"
                  :label="$t('Abilita')"
                  v-model:checked="anagrafica.twofa"
                  @update:checked="setTwoFA(Boolean($event))"
                />
              </form-group>
            </pf-form>

            <twofa-enable-modal v-if="showEnableTwoFA" v-model:open="showEnableTwoFA" v-model:enabled="anagrafica.twofa" />
            <twofa-disable-modal v-if="showDisableTwoFA" v-model:open="showDisableTwoFA" v-model:enabled="anagrafica.twofa" />
          </pf-card-body>
        </pf-card>
      </pf-flex>
    </pf-page-section>
  </page>
</template>

<script lang="ts" setup>
import { ref, type Ref } from 'vue';
import { $t as _t } from '@/i18n';
import ResetPasswordForm from '@/components/reset-password-form.vue';
import TwofaEnableModal from '../components/twofa-enable-modal.vue';
import TwofaDisableModal from '../components/twofa-disable-modal.vue';
import { asyncComputed } from '@common/asyncComputed';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import { useLoginStore } from '@/store/login';
import { type Profile, ProfilesResource } from '@/resources';
import { useAlertStore } from '@/store/alerts';
import { useFunctionRunningState } from '@common/use';

const login = useLoginStore();
const originalMail = ref('');
const originalSubscriptions: Ref<string[]> = ref([]);
const showEnableTwoFA = ref(false);
const showDisableTwoFA = ref(false);

const defaultAnagrafica = {
  id: 0,
  title: null,
  firstname: null,
  lastname: null,
  street: null,
  postalcode: null,
  city: null,
  province: null,
  country: null,
  phone: null,
  mobile: null,
  mail: '',
  newsletter_subscription: false,
  email_subscriptions: [],
  grants: [],
  twofa: false,
} as Profile;

const { result: anagrafica, state: anagraficaState } = asyncComputed(async () => {
  if (!login.id) {
    return defaultAnagrafica;
  }
  const anagrafica = await new ProfilesResource().get(login.id);
  originalMail.value = anagrafica.mail;
  originalSubscriptions.value = [...anagrafica.email_subscriptions];
  return anagrafica;
}, defaultAnagrafica);

const { run: submit } = useFunctionRunningState(
  async () => {
    if (!anagrafica.value.id) {
      return;
    }

    // se non sono owner posso solo rimuovere le notifiche
    if (!login.grants.owner) {
      anagrafica.value.email_subscriptions = anagrafica.value.email_subscriptions.filter((type: string) => originalSubscriptions.value.includes(type));
    }

    const alertStore = useAlertStore();
    try {
      await new ProfilesResource().put(anagrafica.value.id, anagrafica.value);
      alertStore.add({ title: _t('Profilo aggiornato correttamente.'), type: 'success', toast: true });
    } catch {
      alertStore.add({ title: _t('Errore: profilo non aggiornato. Contatta il nostro supporto per maggiori informazioni.'), type: 'danger', toast: true });
    }

    anagraficaState.update();
    await login.refresh();
  },
  { preventMultiple: true },
);

async function setTwoFA(enable: boolean) {
  if (enable) {
    showEnableTwoFA.value = true;
  } else {
    showDisableTwoFA.value = true;
  }
}

function canRemoveSubscriptionOfType(type: string) {
  return login.grants.owner || originalSubscriptions.value.includes(type);
}
</script>
